import React, { useMemo } from 'react';

import { Button } from '@epoch-ui/components/Button';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import { Icon } from '@epoch-ui/components/Icon';

import {
  canManagePostEventReminders,
  POST_EVENT_REMINDER_WINDOW_DAYS
} from '../utils/postEventReminderWindow';

interface CommunicationRemindersEmptyStateProps {
  openCreateEventCommunicationSideSheet: () => void;
}

export const CommunicationRemindersEmptyState: React.FC<CommunicationRemindersEmptyStateProps> = ({
  openCreateEventCommunicationSideSheet
}): React.ReactElement => {
  const { datetimeEnd, hasEnded, isPublished, publishSchedule, scheduledDeletionAt } =
    useEditableEvent();

  const isCancelled = !!scheduledDeletionAt;
  const canManageReminders = canManagePostEventReminders({
    datetimeEnd,
    hasEnded,
    scheduledDeletionAt
  });

  const label = useMemo(() => {
    if (!isPublished) {
      if (publishSchedule?.scheduledAt) {
        return (
          <>
            Event is scheduled for publish
            <p className="tw-m-0 tw-text-xs tw-font-normal tw-leading-4">
              You’ll be able add messages after the event is published
            </p>
          </>
        );
      }

      return 'Publish the event to add messages';
    }

    if (isCancelled) return 'Reminders can’t be added for a cancelled event.';

    if (!canManageReminders)
      return `Reminders can’t be added more than ${POST_EVENT_REMINDER_WINDOW_DAYS} days after an event ends.`;

    return null;
  }, [canManageReminders, isCancelled, isPublished, publishSchedule]);

  return (
    <div>
      <div className="tw-mt-5 tw-flex tw-flex-col tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-dashed tw-border-border-grey tw-bg-background tw-py-8">
        {label ? (
          <>
            <Icon name="message-square-text" />
            <p className="tw-mt-2 tw-text-center tw-text-sm tw-font-normal tw-leading-5 tw-text-primary-grey">
              {label}
            </p>
          </>
        ) : (
          <Button
            variant="secondary"
            size="xxs"
            className="tw-rounded-xl"
            onClick={openCreateEventCommunicationSideSheet}
          >
            <Icon name="plus" size={16} /> Add reminder
          </Button>
        )}
      </div>
    </div>
  );
};
